import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { glassCard, glassCardStatic } from '@/utils/admin-glass-styles';
import { Users, UserCheck, UserPlus, Share2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ReferrerInfo {
  id: string;
  name: string;
  code: string;
  type: 'client' | 'practitioner';
  clientReferrals: number;
  practitionerReferrals: number;
  total: number;
}

interface ReferralStatsData {
  totalReferred: number;
  referredClients: number;
  referredPractitioners: number;
  codesIssued: number;
  topReferrers: ReferrerInfo[];
}

const AdminReferralStats = () => {
  const [stats, setStats] = useState<ReferralStatsData>({
    totalReferred: 0,
    referredClients: 0,
    referredPractitioners: 0,
    codesIssued: 0,
    topReferrers: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      setLoading(true);

      // Fetch referred clients count
      const { count: referredClientsCount } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .like('referral_source', 'REFERRAL:%');

      // Fetch referred practitioners count
      const { count: referredPractitionersCount } = await supabase
        .from('practitioners')
        .select('*', { count: 'exact', head: true })
        .like('referral_source', 'REFERRAL:%');

      // Fetch codes issued (clients + practitioners with referral_code)
      const { count: clientCodes } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .not('referral_code', 'is', null);

      const { count: practitionerCodes } = await supabase
        .from('practitioners')
        .select('*', { count: 'exact', head: true })
        .not('referral_code', 'is', null);

      // Fetch all users with referral codes to build top referrers
      const { data: clientsWithCodes } = await supabase
        .from('clients')
        .select('id, first_name, last_name, referral_code')
        .not('referral_code', 'is', null);

      const { data: practitionersWithCodes } = await supabase
        .from('practitioners')
        .select('id, first_name, last_name, referral_code')
        .not('referral_code', 'is', null);

      // Build referrer list and count their referrals
      const allReferrers: ReferrerInfo[] = [];

      const processReferrer = async (
        user: { id: string; first_name: string | null; last_name: string | null; referral_code: string | null },
        type: 'client' | 'practitioner'
      ) => {
        const pattern = `REFERRAL:%:${user.id}`;
        const { count: cCount } = await supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .like('referral_source', pattern);
        const { count: pCount } = await supabase
          .from('practitioners')
          .select('*', { count: 'exact', head: true })
          .like('referral_source', pattern);

        const clientRefs = cCount || 0;
        const practRefs = pCount || 0;
        const total = clientRefs + practRefs;

        if (total > 0) {
          allReferrers.push({
            id: user.id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
            code: user.referral_code || '',
            type,
            clientReferrals: clientRefs,
            practitionerReferrals: practRefs,
            total,
          });
        }
      };

      const promises: Promise<void>[] = [];
      (clientsWithCodes || []).forEach(c => promises.push(processReferrer(c, 'client')));
      (practitionersWithCodes || []).forEach(p => promises.push(processReferrer(p, 'practitioner')));
      await Promise.all(promises);

      allReferrers.sort((a, b) => b.total - a.total);

      setStats({
        totalReferred: (referredClientsCount || 0) + (referredPractitionersCount || 0),
        referredClients: referredClientsCount || 0,
        referredPractitioners: referredPractitionersCount || 0,
        codesIssued: (clientCodes || 0) + (practitionerCodes || 0),
        topReferrers: allReferrers.slice(0, 10),
      });
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Referred', value: stats.totalReferred, icon: Users, color: 'text-blue-600' },
    { title: 'Referred Clients', value: stats.referredClients, icon: UserPlus, color: 'text-green-600' },
    { title: 'Referred Practitioners', value: stats.referredPractitioners, icon: UserCheck, color: 'text-purple-600' },
    { title: 'Codes Issued', value: stats.codesIssued, icon: Share2, color: 'text-orange-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Referral Statistics</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(card => (
          <Card key={card.title} className={glassCard}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Referrers Table */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="text-lg">Top Referrers</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.topReferrers.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">No referrers found yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Clients</TableHead>
                  <TableHead className="text-right">Practitioners</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.topReferrers.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.code}</code></TableCell>
                    <TableCell className="capitalize">{r.type}</TableCell>
                    <TableCell className="text-right">{r.clientReferrals}</TableCell>
                    <TableCell className="text-right">{r.practitionerReferrals}</TableCell>
                    <TableCell className="text-right font-semibold">{r.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReferralStats;
